import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  discountAdd,
  discountUpdate,
  getDiscountDetail,
} from "../../services/discountService";
import SuccessAlert from "../../components/child/Alert/SuccessAlert";
import DengerAlert from "../../components/child/Alert/DengerAlert";
import Loader from "../../components/child/Loder";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DiscountFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [code, setCode] = useState("");
  const [desc, setDesc] = useState("");
  const [discountType, setDiscountType] = useState(""); // flatfee | percentage
  const [discount, setDisc] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});

  const isFlatFee = discountType === "flatfee";
  const isPercentage = discountType === "percentage";

  useEffect(() => {
    if (!isEditMode) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getDiscountDetail(id);
        const payload = res?.data || {};
        const data =
          payload.result || payload.data || payload.discount || payload;

        setCode(data.code || "");
        setDesc(data.description || "");
        setDisc(
          data.discount != null && data.discount !== ""
            ? String(data.discount)
            : "",
        );

        // API key: discountType — values: flatfee | percentage
        const rawType = String(
          data.discountType ?? data.discountype ?? data.discount_type ?? "",
        )
          .toLowerCase()
          .replace(/[\s_-]/g, "");
        if (rawType === "flatfee" || rawType.includes("flat")) {
          setDiscountType("flatfee");
        } else if (
          rawType === "percentage" ||
          rawType.includes("percent")
        ) {
          setDiscountType("percentage");
        } else {
          setDiscountType("");
        }

        setStartDate(data.startDate ? new Date(data.startDate) : null);
        setEndDate(data.endDate ? new Date(data.endDate) : null);
      } catch (err) {
        setErrorMessage("Failed to fetch discount detail.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEditMode]);

  const handleDiscountChange = (value) => {
    let cleaned = value.replace(/[^0-9.]/g, "");
    const dotIndex = cleaned.indexOf(".");
    const hasDot = dotIndex !== -1;
    const integerPart = hasDot ? cleaned.slice(0, dotIndex) : cleaned;
    const decimalPart = hasDot
      ? cleaned.slice(dotIndex + 1).replace(/\./g, "").slice(0, 2)
      : "";
    cleaned = hasDot ? `${integerPart}.${decimalPart}` : integerPart;

    if (isPercentage && integerPart.length > 3) return;
    if (isFlatFee && integerPart.length > 10) return;
    if (!discountType && integerPart.length > 10) return;

    setDisc(cleaned);
    setErrors((prev) => ({ ...prev, discount: "" }));
  };

  const validateInputs = () => {
    const newErrors = {};

    if (!code.trim()) {
      newErrors.code = "PromoCode is required.";
    }
    if (!desc.trim()) {
      newErrors.desc = "Description is required.";
    }
    if (!discountType) {
      newErrors.discountType = "Discount type is required.";
    }

    if (!discount && discount !== 0) {
      newErrors.discount = "Discount is required.";
    } else {
      const num = Number(discount);
      if (!Number.isFinite(num)) {
        newErrors.discount = "Invalid discount value.";
      } else if (num < 1) {
        newErrors.discount = "Discount minimum value is 1.";
      } else if (discountType === "percentage" && num > 100) {
        newErrors.discount = "Percentage maximum value is 100.";
      } else if (String(discount).split(".")[1]?.length > 2) {
        newErrors.discount = "Maximum 2 digits after decimal are allowed.";
      } else if (discountType === "flatfee") {
        const integerPart = String(discount).split(".")[0] || "";
        if (integerPart.length > 10) {
          newErrors.discount = "Flat fee cannot exceed 10 digits.";
        }
      }
    }

    if (!startDate) {
      newErrors.startDate = "Start Date is required.";
    }
    if (!endDate) {
      newErrors.endDate = "End Date is required.";
    } else if (startDate && endDate && endDate < startDate) {
      newErrors.endDate = "End Date cannot be before Start Date.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;

    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const payload = {
        code,
        description: desc,
        discount,
        discountType, // flatfee | percentage
        startDate,
        endDate,
      };

      let response;
      if (isEditMode) {
        response = await discountUpdate(id, {
          ...payload,
          desc,
        });
      } else {
        response = await discountAdd(payload);
      }

      const successMsg =
        response?.data?.message ||
        response?.data?.data?.message ||
        (isEditMode
          ? "Discount updated successfully!"
          : "Discount added successfully!");

      setSuccessMessage(successMsg);
      setTimeout(() => {
        navigate("/discount");
      }, 1000);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (isEditMode ? "Failed to update discount." : "Failed to add discount.");
      setErrorMessage(msg);
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            {successMessage && <SuccessAlert message={successMessage} />}
            {errorMessage && <DengerAlert message={errorMessage} />}
            <form
              className="row gy-3 needs-validation"
              noValidate
              onSubmit={handleSubmit}
            >
              <div className="col-md-12">
                <label className="form-label">
                  Code<span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.code ? "is-invalid" : ""}`}
                  placeholder="Enter code"
                  value={code}
                  maxLength={10}
                  onChange={(e) => {
                    const filtered = e.target.value
                      .replace(/[^A-Za-z0-9\s]/g, "")
                      .replace(/\s{2,}/g, " ");
                    setCode(filtered);
                    setErrors((prev) => ({ ...prev, code: "" }));
                  }}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/\s{2,}/g, " ")
                      .trimStart();
                  }}
                />
                {errors.code && (
                  <div className="invalid-feedback">{errors.code}</div>
                )}
              </div>

              <div className="col-md-12">
                <label className="form-label">
                  Discount Type<span className="text-danger-600">*</span>
                </label>
                <select
                  className={`form-select ${
                    errors.discountType ? "is-invalid" : ""
                  }`}
                  value={discountType}
                  onChange={(e) => {
                    setDiscountType(e.target.value);
                    setDisc("");
                    setErrors((prev) => ({
                      ...prev,
                      discountType: "",
                      discount: "",
                    }));
                  }}
                >
                  <option value="">Select discount type</option>
                  <option value="flatfee">Flat Fee</option>
                  <option value="percentage">Percentage</option>
                </select>
                {errors.discountType && (
                  <div className="invalid-feedback d-block">
                    {errors.discountType}
                  </div>
                )}
              </div>

              <div className="col-md-12">
                <label className="form-label">
                  Discount
                  {discountType === "flatfee"
                    ? " ($)"
                    : discountType === "percentage"
                      ? " (%)"
                      : ""}
                  <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    errors.discount ? "is-invalid" : ""
                  }`}
                  placeholder={
                    discountType === "flatfee"
                      ? "Enter flat fee"
                      : discountType === "percentage"
                        ? "Enter percentage"
                        : "Select discount type first"
                  }
                  value={discount}
                  maxLength={discountType === "flatfee" ? 13 : 6}
                  disabled={!discountType}
                  onChange={(e) => handleDiscountChange(e.target.value)}
                />
                {errors.discount && (
                  <div className="invalid-feedback d-block">
                    {errors.discount}
                  </div>
                )}
                {discount && discountType && !errors.discount && (
                  <div className="text-secondary-light mt-1 small">
                    Added value:{" "}
                    {discountType === "flatfee"
                      ? `$${discount}`
                      : `${discount}%`}
                  </div>
                )}
              </div>

              <div className="col-md-12 d-flex" style={{ gap: "10px" }}>
                <div className="col-md-6 d-grid">
                  <label className="form-label">
                    Start Date<span className="text-danger-600">*</span>
                  </label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => {
                      setStartDate(date);
                      setEndDate(null);
                      setErrors((prev) => ({ ...prev, startDate: "" }));
                    }}
                    className={`form-control ${
                      errors.startDate ? "is-invalid" : ""
                    }`}
                    placeholderText="Start Date"
                    dateFormat="dd-MM-yyyy"
                  />
                  {errors.startDate && (
                    <div className="invalid-feedback d-block">
                      {errors.startDate}
                    </div>
                  )}
                </div>
                <div className="col-md-6 d-grid">
                  <label className="form-label">
                    End Date<span className="text-danger-600">*</span>
                  </label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => {
                      setEndDate(date);
                      setErrors((prev) => ({ ...prev, endDate: "" }));
                    }}
                    className={`form-control ${
                      errors.endDate ? "is-invalid" : ""
                    }`}
                    placeholderText="End Date"
                    dateFormat="dd-MM-yyyy"
                    minDate={startDate}
                  />
                  {errors.endDate && (
                    <div className="invalid-feedback d-block">
                      {errors.endDate}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-12">
                <label className="form-label">
                  Description<span className="text-danger-600">*</span>
                </label>
                <textarea
                  className={`form-control ${errors.desc ? "is-invalid" : ""}`}
                  cols="4"
                  rows="3"
                  placeholder="Enter description"
                  value={desc}
                  onChange={(e) => {
                    const filtered = e.target.value.replace(/\s{2,}/g, " ");
                    setDesc(filtered);
                    setErrors((prev) => ({ ...prev, desc: "" }));
                  }}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/\s{2,}/g, " ")
                      .trimStart();
                  }}
                />
                {errors.desc && (
                  <div className="invalid-feedback">{errors.desc}</div>
                )}
              </div>

              <div className="col-12">
                <button
                  className="btn btn-primary float-end"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Saving..." : isEditMode ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountFormPage;
